import type { InvoiceComment } from "@/apis/gql/types";
import { useSession } from "@/lib/auth/auth";
import { useTenant } from "@/providers/session";
import { gql, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { GET_ALL_INVOICE_COMMENTS_QUERY } from "./get-all";

type CreateCommentInput = {
  invoiceId: string;
  comment: string;
};

export const useCreateInvoiceComment = () => {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const { tenantId } = useTenant();
  const [createComment, { loading, error }] = useMutation<{
    createInvoiceComment: Partial<InvoiceComment>;
  }>(CREATE_INVOICE_COMMENT_MUTATION);

  const handleCreate = async (
    data: CreateCommentInput,
    onSuccess: () => void = () => {},
    onError: () => void = () => {},
  ) => {
    const toastId = toast.loading(t("generic_loading"));

    const { invoiceId, comment } = data;

    try {
      const response = await createComment({
        variables: {
          input: {
            comment,
          },
          invoiceId,
          tenantId,
        },
        optimisticResponse: {
          createInvoiceComment: {
            invoiceCommentId: crypto.randomUUID(),
            invoiceId: data.invoiceId,
            userId: session?.user?.id ?? crypto.randomUUID(),
            comment: data.comment,
            tenantId,
            createdAt: new Date(Date.now() - 10000).toISOString(),
            updatedAt: new Date(Date.now() - 10000).toISOString(),
          },
        },
        update: (cache, { data: responseData }) => {
          const existingData = cache.readQuery<{
            invoiceComments: InvoiceComment[];
          }>({
            query: GET_ALL_INVOICE_COMMENTS_QUERY,
            variables: {
              invoiceId: data.invoiceId,
              tenantId,
            },
          });

          const newComment = {
            ...responseData?.createInvoiceComment,
            user: {
              userId: session?.user?.id ?? crypto.randomUUID(),
              name: session?.user?.name ?? "Unknown",
              email: session?.user?.email ?? "Unknown",
            },
          };

          if (newComment) {
            cache.writeQuery({
              query: GET_ALL_INVOICE_COMMENTS_QUERY,
              variables: {
                invoiceId: data.invoiceId,
                tenantId,
              },
              data: {
                invoiceComments: [
                  ...(existingData?.invoiceComments || []),
                  newComment,
                ],
              },
            });
          }
        },
      });

      if (response.errors) {
        throw new Error("Error creating comment");
      }

      toast.success(t("generic_success"), {
        id: toastId,
      });

      onSuccess();
      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId,
      });
      throw error;
    }
  };

  return [handleCreate, { loading, error }] as const;
};

export const CREATE_INVOICE_COMMENT_MUTATION = gql`
  mutation CreateInvoiceComment($input: CreateInvoiceCommentInput!, $tenantId: ID!, $invoiceId: ID!) {
    createInvoiceComment(input: $input, tenantId: $tenantId, invoiceId: $invoiceId) {
      invoiceCommentId
      invoiceId
      userId
      comment
      tenantId
      createdAt
      updatedAt
    }
  }
`;
